import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader } from "@/components/Atoms";
import { Package, Sword, AlertTriangle, Search } from "lucide-react";

const TABS = [
  { id: "items", label: "Items",  icon: Sword,    desc: "Saddles, spheres, food, weapons & more — single items." },
  { id: "kits",  label: "Kits",   icon: Package,  desc: "Pre-built bundles for one price." },
];

export default function Shop() {
  const { user } = useAuth();
  const [tab, setTab] = useState("items");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="shop-page">
      <PageHeader
        title="The Shop"
        subtitle="Spend your Chillet Coins — items deliver instantly while you're in-game."
        right={<Currency value={user?.points} className="text-2xl" />}
      />

      {/* Page-level "must be in-game" banner — the per-modal banner
          inside <InGameBanner> still verifies live RCON status when
          the user picks a server, but new players need to know up-front
          that purchases don't queue while they're offline. */}
      <div
        className="glass-card border-amber-400/30 bg-amber-500/5 p-4 mb-6 flex items-start gap-3"
        data-testid="shop-online-required-banner"
      >
        <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <div className="font-semibold text-amber-200 mb-0.5">You must be online when you buy.</div>
          <div className="text-[var(--muted)]">
            Items are delivered directly to your character via RCON the moment you confirm the
            purchase. The Confirm button will stay disabled until we detect your character is
            in-game on the selected server.
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-6 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`shop-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md whitespace-nowrap transition-colors ${
                active ? "bg-[var(--teal-soft)] text-[var(--teal)] border border-[var(--teal)]" : "border border-white/5 hover:border-[var(--teal-soft)]"
              }`}
            >
              <Icon size={16} />
              <span className="text-sm">{t.label}</span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-[var(--muted)] mb-4">
        {TABS.find((t) => t.id === tab)?.desc}
      </p>

      {tab === "items" && <ItemsTab />}
      {tab === "kits" && <KitsTab />}
    </div>
  );
}


// ============================================================================
// ITEMS TAB — individual game items from `kits` table
// ============================================================================

function ItemsTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [items, setItems] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Category filter — same buckets the public Wiki uses, derived from
  // each item's `T_itemicon_<CATEGORY>_*` icon path on the backend.
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/shop/items"), api.get("/servers")])
      .then(([s, srv]) => {
        setItems(s.data.items || []);
        setServers(srv.data.servers || []);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Categories with counts, sorted by descending count then name. We
  // compute this from the already-loaded item list so users get an
  // instant picker that mirrors what they can actually buy right now.
  const categories = useMemo(() => {
    const counts = new Map();
    items.forEach((i) => counts.set(i.category || "Other", (counts.get(i.category || "Other") || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (category && (i.category || "Other") !== category) return false;
      if (!q) return true;
      return i.name.toLowerCase().includes(q) || (i.description || "").toLowerCase().includes(q);
    });
  }, [items, search, category]);

  const buy = async () => {
    if (!selected) return;
    setBuying(true);
    try {
      const { data } = await api.post("/shop/buy/item", {
        item_name: selected.item.name,
        server_name: selected.server,
        quantity: 1,
      });
      toast.success(`Delivered ${data.purchased} on ${data.server} (-${data.cost} coins)`);
      setSelected(null);
      refreshProfile();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBuying(false); }
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="grid sm:grid-cols-[1fr_220px] gap-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${items.length} items…`}
            className="input pl-9 w-full"
            data-testid="shop-items-search"
          />
        </div>
        <select
          className="input"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="shop-items-category"
        >
          <option value="">All categories ({items.length})</option>
          {categories.map(([name, count]) => (
            <option key={name} value={name}>{name} ({count})</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((it, i) => (
          <ShopCard
            key={it.name}
            idx={i}
            kind="item"
            item={it}
            onBuy={() => setSelected({ item: it, server: servers[0]?.name })}
          />
        ))}
        {filtered.length === 0 && <p className="col-span-full text-center text-[var(--muted)] py-8">No items match.</p>}
      </div>

      {selected && (
        <BuyModal
          title="Buy item"
          item={selected.item}
          server={selected.server}
          servers={servers}
          onServer={(s) => setSelected({ ...selected, server: s })}
          onCancel={() => setSelected(null)}
          onConfirm={buy}
          busy={buying}
        />
      )}
    </>
  );
}


// ============================================================================
// KITS TAB — bundles from `admin_kits` table
// ============================================================================

function KitsTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [kits, setKits] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/shop/kits"), api.get("/servers")])
      .then(([k, srv]) => {
        setKits(k.data.kits || []);
        setServers(srv.data.servers || []);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = async () => {
    if (!selected) return;
    setBuying(true);
    try {
      const { data } = await api.post("/shop/buy/kit", {
        item_name: selected.item.name,
        server_name: selected.server,
        quantity: 1,
      });
      toast.success(`Kit delivered: ${data.purchased} (${data.delivered_items.length} items)`);
      setSelected(null);
      refreshProfile();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBuying(false); }
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {kits.map((k, i) => (
          <div
            key={k.name}
            className="glass-card p-5 fade-up flex flex-col"
            style={{ animationDelay: `${i * 40}ms` }}
            data-testid={`shop-kit-${k.name.replace(/\s/g, "-")}`}
          >
            <div className="flex items-start gap-3 mb-3">
              {k.icon_url ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${k.icon_url}`}
                  alt={k.name}
                  className="w-14 h-14 rounded bg-black/40 object-contain p-1 shrink-0"
                  loading="lazy"
                />
              ) : (
                <Package size={24} className="text-[var(--teal)] mt-0.5" style={{ filter: "drop-shadow(0 0 6px var(--teal-glow))" }} />
              )}
              <div className="flex-1">
                <div className="text-base font-semibold">{k.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{k.items_count} items in bundle</div>
              </div>
            </div>
            <p className="text-xs text-[var(--muted)] mb-3 leading-relaxed">{k.description}</p>
            <ul className="text-xs space-y-1 mb-4 max-h-44 overflow-y-auto">
              {k.contents.map((c, idx) => (
                <li key={idx} className="flex items-center gap-2 text-[var(--bone)]">
                  {c.icon_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${c.icon_url}`} alt="" className="w-5 h-5 object-contain shrink-0" />
                  ) : (
                    <span className="w-5 h-5 shrink-0 inline-block" />
                  )}
                  <span className="truncate">{c.label}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between mt-auto">
              <Currency value={k.price} />
              <button
                onClick={() => setSelected({ item: k, server: servers[0]?.name })}
                className="btn btn-sm"
                data-testid={`buy-kit-${k.name.replace(/\s/g, "-")}-button`}
              >
                Buy Kit
              </button>
            </div>
          </div>
        ))}
        {kits.length === 0 && <p className="col-span-full text-center text-[var(--muted)] py-8">No kits available.</p>}
      </div>

      {selected && (
        <BuyModal
          title="Buy kit"
          item={selected.item}
          server={selected.server}
          servers={servers}
          onServer={(s) => setSelected({ ...selected, server: s })}
          onCancel={() => setSelected(null)}
          onConfirm={buy}
          busy={buying}
          contents={selected.item.contents}
        />
      )}
    </>
  );
}


// ============================================================================
// Shared building blocks
// ============================================================================

function ShopCard({ kind, item, idx, onBuy }) {
  const ASSETS = process.env.REACT_APP_BACKEND_URL;
  const Icon = kind === "kit" ? Package : Sword;
  return (
    <div
      className="glass-card p-4 flex flex-col fade-up"
      style={{ animationDelay: `${idx * 20}ms` }}
      data-testid={`shop-${kind}-${item.name.replace(/\s/g, "-")}`}
    >
      <div className="flex items-start gap-3 mb-2">
        {item.icon_url ? (
          <img
            src={`${ASSETS}${item.icon_url}`}
            alt={item.name}
            className="w-12 h-12 rounded bg-black/40 object-contain p-1 shrink-0"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="w-12 h-12 rounded bg-black/40 flex items-center justify-center shrink-0">
            <Icon size={24} className="text-[var(--teal)]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{item.name}</div>
          {item.description && <div className="text-[11px] text-[var(--muted)] line-clamp-2">{item.description}</div>}
        </div>
      </div>
      <div className="flex items-center justify-between mt-auto pt-2">
        <Currency value={item.price} />
        <button
          onClick={onBuy}
          className="btn btn-sm"
          data-testid={`buy-${kind}-${item.name.replace(/\s/g, "-")}-button`}
        >
          Buy
        </button>
      </div>
    </div>
  );
}

function BuyModal({ title, item, server, servers, onServer, onCancel, onConfirm, busy, contents }) {
  // Live "is my character in-game on the selected server?" probe.
  // We disable Confirm until the backend confirms the user is online,
  // so users can't spend coins on a delivery that's guaranteed to fail.
  const [onlineStatus, setOnlineStatus] = useState({ checking: false, linked: false, online: false, player_name: null });

  useEffect(() => {
    if (!server) {
      setOnlineStatus({ checking: false, linked: false, online: false, player_name: null });
      return;
    }
    let cancelled = false;
    const probe = async () => {
      try {
        setOnlineStatus((s) => ({ ...s, checking: true }));
        const { data } = await api.get(`/shop/online-check?server=${encodeURIComponent(server)}`);
        if (cancelled) return;
        setOnlineStatus({ checking: false, ...data });
      } catch {
        if (cancelled) return;
        setOnlineStatus({ checking: false, linked: false, online: false, player_name: null });
      }
    };
    probe();
    // Re-probe every 8s so the user can log in and the button unlocks
    // without closing the modal.
    const t = setInterval(probe, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, [server]);

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="shop-buy-modal"
    >
      <div className="glass-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl mb-1">{title}</h3>
        <p className="text-sm text-[var(--muted)] mb-4">
          Buying <span className="text-[var(--bone)]">{item.name}</span> for <Currency value={item.price} />
        </p>

        {contents && contents.length > 0 && (
          <div className="mb-4 max-h-32 overflow-y-auto rounded bg-black/30 p-3 border border-white/5">
            <div className="label mb-1">You'll receive</div>
            <ul className="text-xs space-y-1">
              {contents.map((c, i) => (
                <li key={i} className="flex items-center gap-2">
                  {c.icon_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${c.icon_url}`} alt="" className="w-4 h-4 object-contain shrink-0" />
                  ) : null}
                  <span>{c.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <label className="label">Deliver to server</label>
        <select
          className="input mb-3 w-full"
          value={server || ""}
          onChange={(e) => onServer(e.target.value)}
          data-testid="shop-buy-server-select"
        >
          <option value="">— select server —</option>
          {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>

        {/* Live online status — only render once a server is picked. */}
        {server && (
          <div
            className={`rounded-md border p-3 text-sm flex items-start gap-2 mb-2 ${
              onlineStatus.online
                ? "border-[var(--teal)]/40 bg-[var(--teal-soft)]"
                : "border-[var(--warning)] bg-[rgba(255,107,53,0.08)]"
            }`}
            data-testid="shop-online-status"
          >
            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${
              onlineStatus.online ? "bg-[var(--teal)] shadow-[0_0_8px_var(--teal-glow)]" : "bg-[var(--warning)]"
            }`} />
            <div className="flex-1">
              {onlineStatus.checking ? (
                <span className="text-[var(--muted)]">Checking your in-game status on <strong>{server}</strong>…</span>
              ) : !onlineStatus.linked ? (
                <>
                  <strong className="text-[var(--warning)]">Not linked.</strong>{" "}
                  Link your account to <strong>{server}</strong> first using <code className="bg-black/40 px-1 rounded">/link</code> in Discord.
                </>
              ) : onlineStatus.online ? (
                <>
                  <strong className="text-[var(--teal)]">{onlineStatus.player_name || "You"} is online.</strong>{" "}
                  Ready to deliver.
                </>
              ) : (
                <>
                  <strong className="text-[var(--warning)]">{onlineStatus.player_name || "Your character"} isn't in-game.</strong>{" "}
                  Join <strong>{server}</strong> then try again — the Confirm button will unlock automatically.
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="btn btn-secondary flex-1" data-testid="shop-buy-cancel">Cancel</button>
          <button
            onClick={onConfirm}
            className="btn flex-1"
            disabled={busy || !server || !onlineStatus.online}
            title={!onlineStatus.online ? "You must be in-game on the selected server" : ""}
            data-testid="shop-buy-confirm"
          >
            {busy ? "Delivering…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
